/**
 * Токены дизайн-системы — единственный источник значений.
 *
 * Их читают и Tailwind (`tailwind.config.ts`), и Mantine (`mantineTheme.ts`),
 * поэтому цвет, радиус или тень меняются в одном месте. Модуль не зависит
 * ни от React, ни от браузера: его загружает и сборщик стилей в Node.
 *
 * Палитра лендинга: тёплый белый и песок, тёплые коричневые чернила,
 * главный акцент — песочный (действие, фокус, выбор), второй — мягкая
 * бирюза (прогресс, успех). Свечения и градиенты строятся из этих
 * двух цветов, поэтому интерфейс яркий, но спокойный. Пары «заливка +
 * текст на ней» (`accent`/`onAccent`, `teal`/`onTeal`) проверены на WCAG AA
 * в обеих темах.
 */

export const palette = {
  /** Основной фон: тёплый белый лендинга. */
  base: '#FDFCF9',
  /** Вторичный фон: песок. */
  sand: '#F7F4ED',
  /** Нижняя ступень градиента фона. */
  sandDeep: '#EFE8DA',
  /** Карточки и поверхности. */
  surface: '#FFFFFF',
  surfaceMuted: '#FBF9F4',

  /** Главный акцент: тёплый песочный лендинга — кнопки, выбор. Тёмно-коричневый текст на нём — 7:1. */
  accent: '#E8B87A',
  accentHover: '#D99A5C',
  /** Акцент для текста и значков на светлом: 5.2:1 на белом. */
  accentDark: '#9A5F24',
  onAccent: '#4A3B31',

  /** Прогресс и успех: мягкая бирюза лендинга. Тёмный текст на ней — 8:1. */
  teal: '#8CC4C4',
  /** Бирюза для текста и значков на светлом (teal-ink лендинга): 5.9:1. */
  tealDeep: '#2F6E6F',
  onTeal: '#1F3F40',
  /** Верный ответ. */
  correct: '#3E9C7A',
  onCorrect: '#0E2A20',

  /** Заголовки и текст: тёплые чернила лендинга, 8:1 на фоне. */
  heading: '#4A3B31',
  /** Подписи: 5.6:1 на белом. */
  muted: '#705F51',
  locked: '#EFE8DC',
  lockedInk: '#A8998B',

  hairline: '#F0EAE0',
  line: '#E3D9CB',
  danger: '#E0705F',
  dangerDark: '#A6544A',
  /** Значок на розовой заливке ошибки. */
  onDanger: '#FFFFFF',
} as const;

export type PaletteKey = keyof typeof palette;
export type Palette = Record<PaletteKey, string>;

/** Тёмная тема лендинга: тёплый тёмный шоколад, акценты — песок и бирюза. */
export const paletteDark: Palette = {
  base: '#1A1714',
  sand: '#241F1B',
  sandDeep: '#2C2621',
  surface: '#2C2621',
  surfaceMuted: '#332C26',
  accent: '#E8B87A',
  accentHover: '#F0C894',
  accentDark: '#F0C894',
  /** На песочном — тёмный текст: 8:1. */
  onAccent: '#2D221A',
  teal: '#8CC4C4',
  tealDeep: '#8CC4C4',
  onTeal: '#13292A',
  correct: '#6CC6A0',
  onCorrect: '#0B2219',
  heading: '#F0E9E0',
  muted: '#BEB1A4',
  locked: '#332C26',
  lockedInk: '#857668',
  hairline: '#332C26',
  line: '#453C34',
  danger: '#F0A596',
  dangerDark: '#F6BDB1',
  /** На светлом розовом тёмной темы — тёмный значок. */
  onDanger: '#3A140E',
};

export type ThemeMode = 'light' | 'dark';

export const PALETTES: Record<ThemeMode, Palette> = { light: palette, dark: paletteDark };

/** Радиусы: вложенный элемент всегда на ступень меньше родителя. */
export const radii = {
  card: '28px',
  tile: '20px',
  button: '16px',
  input: '14px',
  chip: '10px',
} as const;

/** Шкала типографики (rem). Заголовки — fluid через clamp. */
export const typeScale = {
  display: ['clamp(2.25rem, 1.6rem + 2.4vw, 3.75rem)', { lineHeight: '1.04', letterSpacing: '-0.032em', fontWeight: '700' }],
  title: ['clamp(1.75rem, 1.4rem + 1.2vw, 2.5rem)', { lineHeight: '1.1', letterSpacing: '-0.026em', fontWeight: '700' }],
  headline: ['1.375rem', { lineHeight: '1.25', letterSpacing: '-0.016em', fontWeight: '650' }],
  subtitle: ['1.25rem', { lineHeight: '1.4', letterSpacing: '-0.01em', fontWeight: '500' }],
  body: ['1.0625rem', { lineHeight: '1.55', letterSpacing: '-0.006em' }],
  callout: ['0.9375rem', { lineHeight: '1.45' }],
  caption: ['0.8125rem', { lineHeight: '1.35', letterSpacing: '0.005em', fontWeight: '500' }],
  overline: ['0.75rem', { lineHeight: '1.2', letterSpacing: '0.12em', fontWeight: '650' }],
  button: ['1.0625rem', { lineHeight: '1.2', letterSpacing: '-0.006em', fontWeight: '600' }],
} as const;

/** Чернильная тень (объём), фиолетовое и аква-свечение (акценты). */
const INK = '74,59,49';
const SAND = '232,184,122';
const TEAL = '140,196,196';

export const shadows = {
  /** Мягкая тень без цветного ореола: контакт и лёгкий объём — карточки чистые. */
  soft: `0 1px 2px rgba(${INK},0.04), 0 6px 18px -10px rgba(${INK},0.10)`,
  softLg: `0 2px 4px rgba(${INK},0.04), 0 14px 32px -16px rgba(${INK},0.14)`,
  node: `0 2px 4px rgba(${INK},0.06), 0 12px 28px -6px rgba(${TEAL},0.45)`,
  button: `0 1px 2px rgba(${INK},0.10), 0 8px 20px -6px rgba(${SAND},0.55)`,
  buttonHover: `0 2px 4px rgba(${INK},0.10), 0 14px 32px -8px rgba(${SAND},0.65)`,
  buttonPressed: `0 1px 2px rgba(${INK},0.14)`,
  glow: `0 0 0 4px rgba(${SAND},0.25)`,
  reward: `0 8px 24px -6px rgba(${TEAL},0.55)`,
} as const;

export const gradients = {
  page: [
    `radial-gradient(70% 50% at 0% 0%, rgba(${SAND},0.10) 0%, rgba(${SAND},0) 70%)`,
    `radial-gradient(60% 45% at 100% 100%, rgba(${TEAL},0.10) 0%, rgba(${TEAL},0) 70%)`,
    `linear-gradient(180deg, ${palette.base} 0%, ${palette.sand} 100%)`,
  ].join(', '),
  welcome: [
    `radial-gradient(60% 50% at 50% 30%, rgba(${SAND},0.14) 0%, rgba(${SAND},0) 70%)`,
    `linear-gradient(180deg, ${palette.base} 0%, ${palette.sand} 100%)`,
  ].join(', '),
  cardWarm: `linear-gradient(160deg, ${palette.surface} 0%, ${palette.surfaceMuted} 100%)`,
  logoMark: `linear-gradient(135deg, ${palette.accent} 0%, #F1E0BB 45%, ${palette.teal} 100%)`,
  /** Главная кнопка: светлый песок с переходом в карамель. */
  accent: `linear-gradient(135deg, #EFC490 0%, ${palette.accent} 55%, #D99A5C 100%)`,
  /** Полоса прогресса: бирюза → светлый песок → песок. */
  progress: `linear-gradient(90deg, ${palette.teal} 0%, #F1E0BB 55%, ${palette.accent} 100%)`,
} as const;

export const glass = {
  background: 'rgba(253,252,249,0.7)',
  blur: '24px',
} as const;

type ShadowKey = keyof typeof shadows;
type GradientKey = keyof typeof gradients;

const SAND_DARK = '232,184,122';
const TEAL_DARK = '140,196,196';

const shadowsDark: Record<ShadowKey, string> = {
  soft: '0 1px 2px rgba(0,0,0,0.4), 0 8px 20px -10px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)',
  softLg: '0 2px 6px rgba(0,0,0,0.4), 0 16px 36px -16px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
  node: `0 0 0 1px rgba(${TEAL_DARK},0.35), 0 10px 30px -6px rgba(${TEAL_DARK},0.5)`,
  button: `0 1px 2px rgba(0,0,0,0.5), 0 6px 16px -8px rgba(${SAND_DARK},0.22)`,
  buttonHover: `0 2px 4px rgba(0,0,0,0.5), 0 10px 22px -10px rgba(${SAND_DARK},0.32)`,
  buttonPressed: '0 1px 2px rgba(0,0,0,0.6)',
  glow: `0 0 0 4px rgba(${SAND_DARK},0.32)`,
  reward: `0 8px 24px -6px rgba(${TEAL_DARK},0.45)`,
};

const gradientsDark: Record<GradientKey, string> = {
  page: [
    `radial-gradient(70% 50% at 0% 0%, rgba(${SAND_DARK},0.16) 0%, rgba(${SAND_DARK},0) 70%)`,
    `radial-gradient(60% 45% at 100% 100%, rgba(${TEAL_DARK},0.10) 0%, rgba(${TEAL_DARK},0) 70%)`,
    `linear-gradient(180deg, ${paletteDark.base} 0%, ${paletteDark.sand} 100%)`,
  ].join(', '),
  welcome: [
    `radial-gradient(60% 50% at 50% 30%, rgba(${SAND_DARK},0.18) 0%, rgba(${SAND_DARK},0) 70%)`,
    `linear-gradient(180deg, ${paletteDark.base} 0%, ${paletteDark.sand} 100%)`,
  ].join(', '),
  cardWarm: `linear-gradient(160deg, ${paletteDark.surface} 0%, ${paletteDark.surfaceMuted} 100%)`,
  logoMark: `linear-gradient(135deg, ${paletteDark.accent} 0%, #F1E0BB 45%, ${paletteDark.teal} 100%)`,
  accent: `linear-gradient(135deg, #F0C894 0%, ${paletteDark.accent} 55%, #D99A5C 100%)`,
  progress: `linear-gradient(90deg, ${paletteDark.teal} 0%, #F1E0BB 55%, ${paletteDark.accent} 100%)`,
};

function channels(hex: string): string {
  const value = Number.parseInt(hex.slice(1), 16);
  return `${(value >> 16) & 255} ${(value >> 8) & 255} ${value & 255}`;
}

function kebab(key: string): string {
  return key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

/** CSS-переменные темы: цвета — каналами RGB, чтобы работали `bg-accent/25`. */
export function themeVars(mode: ThemeMode): Record<string, string> {
  const colors = PALETTES[mode];
  const shadowSet = mode === 'dark' ? shadowsDark : shadows;
  const gradientSet = mode === 'dark' ? gradientsDark : gradients;
  return {
    ...Object.fromEntries(Object.entries(colors).map(([key, hex]) => [`--c-${kebab(key)}`, channels(hex)])),
    ...Object.fromEntries(Object.entries(shadowSet).map(([key, value]) => [`--shadow-${kebab(key)}`, value])),
    ...Object.fromEntries(Object.entries(gradientSet).map(([key, value]) => [`--grad-${kebab(key)}`, value])),
    '--glass-bg': mode === 'dark' ? 'rgba(26,23,20,0.7)' : glass.background,
    '--sheen': mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.55)',
  };
}

/** Ссылка на цвет темы для Tailwind. */
export function colorVar(key: PaletteKey): string {
  return `rgb(var(--c-${kebab(key)}) / <alpha-value>)`;
}

export function shadowVar(key: ShadowKey): string {
  return `var(--shadow-${kebab(key)})`;
}

export function gradientVar(key: GradientKey): string {
  return `var(--grad-${kebab(key)})`;
}

/**
 * Один гротеск в духе SF Pro: Inter с плотным трекингом для заголовков.
 * Manrope остаётся только для «игровых» цифр HUD — XP, уровни, счётчики.
 */
export const fonts = {
  heading: ['Inter Variable', 'Inter', '-apple-system', 'system-ui', 'sans-serif'],
  body: ['Inter Variable', 'Inter', '-apple-system', 'system-ui', 'sans-serif'],
  hud: ['Manrope Variable', 'Manrope', 'Inter Variable', 'system-ui', 'sans-serif'],
} as const;
